import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import type { CardAction } from '@omni/ai/features/card.schema';
import { NovaMarkdownPipe } from '../../nova-markdown.pipe';
import { CardActionsComponent } from '../card-actions.component';

type InsightSeverity = 'urgent' | 'warning' | 'recommended';

/**
 * One severity-tagged insight card: pill + headline + observation (markdown),
 * optional action line, non-interactive `tools[]` chips, and tappable
 * `actions[]` chips (emitted up via `action`). Severity helpers + palette are
 * defined here in `card-insight`. The array is rendered in agent order — NO
 * severity sort.
 */
@Component({
  selector: 'card-insight',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NovaMarkdownPipe, CardActionsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './card-insight.component.html',
  styleUrls: ['./card-insight.component.scss'],
})
export class CardInsightComponent {
  @Input() severity: InsightSeverity = 'recommended';
  @Input() headline = '';
  @Input() observation = '';
  @Input() insightAction: string | null = null;
  @Input() tools: string[] | null = null;
  @Input() actions: CardAction[] | null = null;

  @Output() action = new EventEmitter<CardAction>();

  get severityClass(): string {
    return 'card-insight--' + this.severity;
  }

  get pillClass(): string {
    return 'card-insight__pill--' + this.severity;
  }

  /** Translation key of the pill label for severity `s`. */
  severityLabelKey(s: InsightSeverity): string {
    switch (s) {
      case 'urgent':
        return 'NOVA_INSIGHT_URGENT';
      case 'warning':
        return 'NOVA_INSIGHT_WARNING';
      case 'recommended':
        return 'NOVA_INSIGHT_RECOMMENDED';
    }
  }

  severityGlyph(s: InsightSeverity): string {
    switch (s) {
      case 'urgent':
        return '🚨';
      case 'warning':
        return '⚠️';
      case 'recommended':
        return '🎯';
    }
  }

  trackTool(_i: number, tool: string): string {
    return tool;
  }

  onAction(a: CardAction): void {
    this.action.emit(a);
  }
}
